import i18next, { createInstance, type i18n as I18nInstance } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import {
    DEFAULT_NAMESPACE,
    NAMESPACES,
    normalizeLocale,
    resources,
    SUPPORTED_LOCALES,
    type Locale,
} from './resources';

// 资源全部静态打包并内联进 init（不再走异步 import()），因此 i18next 在初始化后
// 第一次 render 就能同步返回译文 —— 彻底消除「先英文 key、再切中文」的首屏闪烁。
const baseOptions = {
    resources,
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: NAMESPACES as unknown as string[],
    defaultNS: DEFAULT_NAMESPACE,
    // 不设全局 fallbackNS：Studio 通过 useI18n('studio') 内部用 ['studio','translation'] 命名空间数组
    // 做「按需」回退（见 providers/i18n.tsx），避免全局 fallbackNS 影响 market 等其它命名空间。
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    // 资源已内联，关闭异步初始化让 init 同步完成，保证首帧即就绪。
    initImmediate: false,
    react: { useSuspense: false },
} as const;

// 服务端：按 locale 缓存的独立实例（每个受支持语言至多一个）。
// 关键：绝不能在服务端复用「会被 changeLanguage 改写的全局单例」，否则并发请求会串语言
// （请求 A 设为 zh、请求 B 设为 en，渲染交错时互相污染）。这些实例只读、不切换语言，
// 因此可安全跨请求复用，既无串台风险又零额外开销。
const serverInstances = new Map<Locale, I18nInstance>();

export function getServerI18n(locale: string | null | undefined): I18nInstance {
    const normalized = normalizeLocale(locale);
    const cached = serverInstances.get(normalized);
    if (cached) return cached;

    const instance = createInstance();
    instance.use(initReactI18next).init({ ...baseOptions, lng: normalized });
    serverInstances.set(normalized, instance);
    return instance;
}

// 客户端：全局单例，只初始化一次；支持 changeLanguage 与语言检测（querystring / localStorage 缓存）。
const ensureInit = (defaultLocale?: string) => {
    if (i18next.isInitialized) return;

    i18next
        .use(LanguageDetector)
        .use(initReactI18next)
        .init({
            ...baseOptions,
            // defaultLocale 由服务端按 cookie/Accept-Language 解析后下发，作为首帧权威语言，
            // 保证 SSR 与客户端 hydration 文本一致（不会触发 #418）。
            lng: defaultLocale ? normalizeLocale(defaultLocale) : undefined,
            detection: {
                order: defaultLocale
                    ? ['querystring', 'localStorage']
                    : ['querystring', 'localStorage', 'navigator'],
                caches: ['localStorage'],
                lookupQuerystring: 'lang',
                lookupLocalStorage: 'locale',
            },
        });
};

export const initI18n = (defaultLocale?: string) => {
    ensureInit(defaultLocale);
    return i18next;
};

export const getLanguages = async () => {
    const languages = (await import(`@/lib/i18n/locales/languages.json`)).default;
    return languages;
};

export const changeLanguage = (lang: string) => {
    const normalized = normalizeLocale(lang);
    if (typeof document !== 'undefined') {
        document.documentElement.setAttribute('lang', normalized);
    }
    try {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem('locale', normalized);
        }
    } catch { }
    try {
        if (typeof document !== 'undefined') {
            // cookie 是服务端 SSR 判定语言的权威来源，必须写入，确保下次加载首帧语言正确。
            document.cookie = `locale=${normalized}; path=/; max-age=31536000`;
        }
    } catch { }
    return i18next.changeLanguage(normalized);
};

export default i18next;
